/**
 * Event booking
 * Renders bookable appointment slots for each team member and creates the
 * booking via the Google Apps Script web app. See sections/event-booking.liquid.
 *
 * Network calls are intentionally "simple requests" (GET, or POST with a
 * text/plain body) so the browser never sends a CORS preflight to Apps Script.
 */
class EventBooking extends HTMLElement {
  #endpoint = '';
  #token = '';
  #eventName = '';
  #eventStart = '';
  #eventEnd = '';
  #location = '';
  #allowWeekend = false;
  #dialog = null;
  #strings = {};
  #activeCalendarId = '';
  #activeMemberName = '';
  #chosen = null;

  connectedCallback() {
    this.#endpoint = this.dataset.endpoint || '';
    this.#token = this.dataset.token || '';
    this.#eventName = this.dataset.eventName || '';
    this.#eventStart = this.dataset.eventStart || '';
    this.#eventEnd = this.dataset.eventEnd || '';
    this.#location = this.dataset.location || '';
    this.#allowWeekend = this.dataset.allowWeekend === 'true' || this.dataset.allowWeekend === '1';
    this.#dialog = this.querySelector('[data-booking-dialog]');
    this.#strings = this.#parseStrings();

    if (!this.#dialog) {
      return;
    }

    this.#setupEventSelect();

    for (const button of this.querySelectorAll('[data-booking-open]')) {
      button.addEventListener('click', () => {
        this.#open(button.dataset.calendarId, button.dataset.memberName || '');
      });
    }

    for (const button of this.#dialog.querySelectorAll('[data-booking-close]')) {
      button.addEventListener('click', () => this.#dialog.close());
    }

    // Release the body scroll lock whenever the dialog closes (button, ESC or backdrop).
    this.#dialog.addEventListener('close', () => this.#onClose());
    this.#dialog.addEventListener('cancel', () => this.#onClose());

    const backButton = this.#dialog.querySelector('[data-booking-back]');

    if (backButton) {
      backButton.addEventListener('click', () => this.#showView('slots'));
    }

    const form = this.#dialog.querySelector('[data-booking-form]');

    if (form) {
      form.addEventListener('submit', (event) => this.#onSubmit(event));
    }

    // Close when the backdrop (the dialog element itself) is clicked.
    this.#dialog.addEventListener('click', (event) => {
      if (event.target === this.#dialog) {
        this.#dialog.close();
      }
    });
  }

  // Team-member page: wire up the event picker so the customer can choose which
  // event to book. Pre-selects the event named in an ?event=<handle> deep link
  // (e.g. when arriving from a specific event page), then keeps the visible
  // panel and booking date range in sync with the current selection.
  #setupEventSelect() {
    const select = this.querySelector('[data-event-select]');

    if (!select) {
      return;
    }

    const wanted = new URLSearchParams(window.location.search).get('event');

    if (wanted) {
      for (const option of select.options) {
        if (option.value === wanted) {
          select.value = wanted;
          break;
        }
      }
    }

    select.addEventListener('change', () => this.#syncSelectedEvent());
    this.#syncSelectedEvent();
  }

  #syncSelectedEvent() {
    const select = this.querySelector('[data-event-select]');
    const option = select && select.selectedOptions[0];

    if (!option) {
      return;
    }

    this.#eventName = option.dataset.eventName || '';
    this.#eventStart = option.dataset.eventStart || '';
    this.#eventEnd = option.dataset.eventEnd || '';
    this.#location = option.dataset.location || '';
    this.#allowWeekend = option.dataset.allowWeekend === 'true' || option.dataset.allowWeekend === '1';

    const nameEl = this.querySelector('[data-event-name-display]');

    if (nameEl) {
      nameEl.textContent = this.#eventName;
    }

    const datesEl = this.querySelector('[data-event-dates-display]');

    if (datesEl) {
      datesEl.textContent = option.dataset.dates || '';
    }

    const locationEl = this.querySelector('[data-event-location-display]');

    if (locationEl) {
      locationEl.textContent = this.#location;
    }

    const locationField = this.querySelector('[data-event-location-field]');

    if (locationField) {
      locationField.hidden = this.#location === '';
    }
  }

  #open(calendarId, memberName) {
    if (!this.#endpoint || !calendarId) {
      return;
    }

    this.#activeCalendarId = calendarId;
    this.#activeMemberName = memberName;
    this.#chosen = null;

    const title = this.#dialog.querySelector('[data-booking-title]');

    if (title) {
      title.textContent = this.#strings.dialogTitle.replace('{name}', memberName);
    }

    this.#showView('slots');

    document.body.classList.add('dialog-open');

    if (typeof this.#dialog.showModal === 'function') {
      this.#dialog.showModal();
    } else {
      this.#dialog.setAttribute('open', '');
    }

    this.#loadSlots();
  }

  #onClose() {
    if (!document.querySelector('dialog[open]')) {
      document.body.classList.remove('dialog-open');
    }
  }

  #loadSlots() {
    const days = this.#dialog.querySelector('[data-booking-days]');
    days.innerHTML = '';
    this.#setStatus(this.#strings.loading);
    this.#setLoading(true);

    const params = new URLSearchParams({
      action: 'slots',
      token: this.#token,
      calendarId: this.#activeCalendarId,
      start: this.#eventStart || this.#todayString(),
      end: this.#eventEnd || this.#defaultEndString(),
      weekend: this.#allowWeekend ? '1' : '0',
    });

    fetch(`${this.#endpoint}?${params.toString()}`)
      .then((response) => response.json())
      .then((data) => {
        if (!data || !data.ok) {
          throw new Error((data && data.error) || 'request_failed');
        }

        this.#renderSlots(data.slots || []);
      })
      .catch(() => {
        this.#setStatus(this.#strings.error);
      })
      .finally(() => {
        this.#setLoading(false);
      });
  }

  #setLoading(state) {
    const spinner = this.#dialog.querySelector('[data-booking-spinner]');

    if (spinner) {
      spinner.hidden = !state;
    }
  }

  #renderSlots(slots) {
    const days = this.#dialog.querySelector('[data-booking-days]');
    days.innerHTML = '';

    if (slots.length === 0) {
      this.#setStatus(this.#strings.noSlots);
      return;
    }

    this.#setStatus('');

    const grouped = new Map();

    for (const slot of slots) {
      if (!grouped.has(slot.day)) {
        grouped.set(slot.day, { label: slot.dayLabel, slots: [] });
      }

      grouped.get(slot.day).slots.push(slot);
    }

    const fragment = document.createDocumentFragment();

    for (const group of grouped.values()) {
      const dayEl = document.createElement('div');
      dayEl.className = 'event-booking__day';

      const labelEl = document.createElement('p');
      labelEl.className = 'event-booking__day-label';
      labelEl.textContent = group.label;
      dayEl.appendChild(labelEl);

      const listEl = document.createElement('div');
      listEl.className = 'event-booking__slots';

      for (const slot of group.slots) {
        const slotButton = document.createElement('button');
        slotButton.type = 'button';
        slotButton.className = 'event-booking__slot';
        slotButton.textContent = slot.label;
        slotButton.addEventListener('click', () => this.#chooseSlot(slot));
        listEl.appendChild(slotButton);
      }

      dayEl.appendChild(listEl);
      fragment.appendChild(dayEl);
    }

    days.appendChild(fragment);
  }

  #chooseSlot(slot) {
    this.#chosen = slot;

    const chosenEl = this.#dialog.querySelector('[data-booking-chosen]');

    if (chosenEl) {
      chosenEl.textContent = this.#strings.chosen
        .replace('{day}', slot.dayLabel)
        .replace('{time}', slot.label)
        .replace('{name}', this.#activeMemberName);
    }

    this.#clearFormError();
    this.#showView('form');
  }

  #onSubmit(event) {
    event.preventDefault();

    const form = event.currentTarget;

    // Honeypot: a real person never fills this.
    if (form.elements.website && form.elements.website.value) {
      return;
    }

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    if (!this.#chosen) {
      return;
    }

    const submit = form.querySelector('[data-booking-submit]');
    const originalLabel = submit.textContent;
    submit.disabled = true;
    submit.classList.add('is-loading');
    submit.textContent = this.#strings.booking;
    // Lock the fields so nothing can be edited mid-submit.
    form.classList.add('is-submitting');
    Array.from(form.elements).forEach((element) => {
      element.disabled = true;
    });
    this.#clearFormError();

    const payload = {
      action: 'book',
      token: this.#token,
      calendarId: this.#activeCalendarId,
      start: this.#chosen.start,
      name: form.elements.name.value.trim(),
      email: form.elements.email.value.trim(),
      company: form.elements.company.value.trim(),
      notes: form.elements.notes.value.trim(),
      eventName: this.#eventName,
      location: this.#location,
      weekend: this.#allowWeekend ? '1' : '0',
    };

    fetch(this.#endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(payload),
    })
      .then((response) => response.text().then((text) => ({ status: response.status, text: text })))
      .then(({ status, text }) => {
        let data = null;

        try {
          data = JSON.parse(text);
        } catch (parseError) {
          // Surface exactly what came back so a non-JSON response (e.g. an Apps
          // Script redirect/HTML page) is visible instead of a silent failure.
          console.error('event-booking: unreadable booking response', status, text);
        }

        if (data && data.ok) {
          this.#showConfirmation();
          form.reset();
          return;
        }

        if (data && data.error === 'slot_taken') {
          this.#showFormError(this.#strings.slotTaken);
          this.#showView('slots');
          this.#loadSlots();
          return;
        }

        if (data && data.error) {
          console.error('event-booking: booking rejected', data.error);
        }

        this.#showFormError(this.#strings.bookingError);
      })
      .catch((requestError) => {
        console.error('event-booking: booking request failed', requestError);
        this.#showFormError(this.#strings.bookingError);
      })
      .finally(() => {
        submit.disabled = false;
        submit.classList.remove('is-loading');
        submit.textContent = originalLabel;
        form.classList.remove('is-submitting');
        Array.from(form.elements).forEach((element) => {
          element.disabled = false;
        });
      });
  }

  #showConfirmation() {
    const detail = this.#dialog.querySelector('[data-booking-confirm-detail]');

    if (detail && this.#chosen) {
      detail.textContent = this.#strings.confirmDetail
        .replace('{day}', this.#chosen.dayLabel)
        .replace('{time}', this.#chosen.label)
        .replace('{name}', this.#activeMemberName);
    }

    this.#showView('confirm');
  }

  #showView(view) {
    for (const el of this.#dialog.querySelectorAll('[data-booking-view]')) {
      el.hidden = el.dataset.bookingView !== view;
    }
  }

  #setStatus(message) {
    const status = this.#dialog.querySelector('[data-booking-status]');

    if (!status) {
      return;
    }

    status.textContent = message;
    status.hidden = message === '';
  }

  #showFormError(message) {
    const error = this.#dialog.querySelector('[data-booking-form-error]');

    if (!error) {
      return;
    }

    error.textContent = message;
    error.hidden = false;
  }

  #clearFormError() {
    const error = this.#dialog.querySelector('[data-booking-form-error]');

    if (!error) {
      return;
    }

    error.textContent = '';
    error.hidden = true;
  }

  #parseStrings() {
    const node = this.querySelector('[data-event-booking-strings]');

    if (!node) {
      return {};
    }

    try {
      const parsed = JSON.parse(node.textContent);

      // Shopify's `t` filter HTML-escapes output (e.g. ' -> &#39;). These strings
      // are inserted via textContent, so decode entities back to real characters.
      for (const key of Object.keys(parsed)) {
        if (typeof parsed[key] === 'string') {
          parsed[key] = this.#decodeEntities(parsed[key]);
        }
      }

      return parsed;
    } catch (error) {
      return {};
    }
  }

  #decodeEntities(value) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  }

  #todayString() {
    return new Date().toISOString().slice(0, 10);
  }

  #defaultEndString() {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().slice(0, 10);
  }
}

customElements.define('event-booking', EventBooking);
